import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatDate, formatNumber } from "../lib/utils";
import { Search, ChevronLeft, ChevronRight, Plus } from "lucide-react";

interface BlogPostRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string;
  tags: string[] | null;
  author: string;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BlogPostsResponse {
  posts: BlogPostRow[];
  total: number;
  page: number;
  totalPages: number;
}

function getStatusBadge(isPublished: boolean) {
  if (isPublished) {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        Опубликовано
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
      Черновик
    </span>
  );
}

function getCategoryLabel(category: string) {
  const map: Record<string, string> = {
    ai: "ИИ",
    sales: "Продажи",
    support: "Поддержка",
    voice: "Голос",
    guide: "Гайд",
  };
  return map[category] || category;
}

export default function BlogPostsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const limit = 20;

  const searchQuery = search.length >= 2 ? search : "";

  const { data, isLoading } = useQuery<BlogPostsResponse>({
    queryKey: [
      `/api/admin/blog?page=${page}&limit=${limit}${status ? `&status=${status}` : ""}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ""}`,
    ],
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Блог</h1>
          {data && (
            <span className="text-sm text-muted-foreground">
              Всего: {formatNumber(data.total)}
            </span>
          )}
        </div>
        <Link href="/blog/new">
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Новый пост
          </button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск по заголовку..."
            className="pl-9 pr-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring w-64"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все</option>
          <option value="published">Опубликовано</option>
          <option value="draft">Черновики</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Заголовок
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Категория
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Дата
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.posts.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Посты не найдены
                  </td>
                </tr>
              ) : (
                data?.posts.map((post) => (
                  <tr key={post.id} className="hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <Link href={`/blog/${post.id}`}>
                        <span className="text-sm text-primary hover:underline cursor-pointer font-medium">
                          {post.title}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {getCategoryLabel(post.category)}
                    </td>
                    <td className="px-4 py-2">
                      {getStatusBadge(post.is_published)}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {post.published_at
                        ? formatDate(post.published_at)
                        : formatDate(post.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Стр. {data.page} из {data.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(data.totalPages, p + 1))
              }
              disabled={page >= data.totalPages}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
